import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingCart, Tag, MessageSquare, LogOut, Menu, X, Percent } from 'lucide-react';
import { useAdmin } from '@/context/AdminContext';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout } = useAdmin();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/products', label: 'Produtos', icon: Package },
    { path: '/admin/orders', label: 'Pedidos', icon: ShoppingCart },
    { path: '/admin/coupons', label: 'Cupons', icon: Tag },
    { path: '/admin/reviews', label: 'Avaliações', icon: MessageSquare },
    { path: '/admin/upsell', label: 'Upsell', icon: Percent },
  ];

  const handleMenuClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-zinc-900 border-b border-white/10 z-50 flex items-center justify-between px-4">
        <Link to="/admin/dashboard" className="flex items-center gap-2">
          <h1 className="font-heading text-lg font-bold text-primary">DON'T</h1>
          <h1 className="font-heading text-lg font-bold text-white">ADMIN</h1>
        </Link>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-white p-2"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/80 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Slide Menu */}
      <aside className={`
        lg:hidden fixed top-16 left-0 bottom-0 w-64 bg-zinc-900 border-r border-white/10 z-50
        transform transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-4">
          <p className="text-xs text-zinc-500">Olá, {admin?.username}</p>
        </div>

        <nav className="px-3">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => handleMenuClick(item.path)}
                className={`flex items-center gap-3 px-3 py-3 rounded mb-1 transition-colors w-full text-left ${
                  isActive
                    ? 'bg-primary text-black font-bold'
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`mobile-menu-${item.label.toLowerCase()}`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-sm uppercase tracking-wider">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-3 rounded w-full text-zinc-400 hover:text-white hover:bg-red-500/10 transition-colors"
            data-testid="mobile-logout-button"
          >
            <LogOut className="h-5 w-5" />
            <span className="text-sm uppercase tracking-wider">Sair</span>
          </button>
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 h-full w-64 bg-zinc-900 border-r border-white/10 z-50">
        <div className="p-6">
          <Link to="/admin/dashboard" className="flex items-center gap-2">
            <h1 className="font-heading text-xl font-bold text-primary">DON'T</h1>
            <h1 className="font-heading text-xl font-bold text-white">ADMIN</h1>
          </Link>
          <p className="text-xs text-zinc-500 mt-1">Olá, {admin?.username}</p>
        </div>

        <nav className="px-3">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-3 rounded mb-1 transition-colors ${
                  isActive
                    ? 'bg-primary text-black font-bold'
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`menu-${item.label.toLowerCase()}`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-sm uppercase tracking-wider">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-3 rounded w-full text-zinc-400 hover:text-white hover:bg-red-500/10 transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="h-5 w-5" />
            <span className="text-sm uppercase tracking-wider">Sair</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-20 lg:pt-0 p-4 lg:p-8">
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
